import { LoadingButton } from "@mui/lab";
import {
    Alert,
    Button,
    Checkbox,
    Container,
    Divider,
    Paper,
    Stack,
    Typography
} from "@mui/material";
import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
    AccountDetailDto,
    AccountService,
    AccountSettingsDto,
    AccountSettingsExpirationInMonths,
    ProjectService,
    SetAccountSettingInput,
} from "../../../api/psc";
import { PageHeader, Spinner } from "../../../components/exports";
import { DialogContext } from "../../../contexts/DialogContext";
import { SnackbarContext } from "../../../contexts/SnackbarContext";
import { UserContext } from "../../../contexts/UserContext";
import { RootState } from "../../../slices/Store";
import { toJalaliDate } from "../../../utils/date";
import AccountResetDialog from "../dialogs/AccountResetDialog";
import FreezeAccountDialog from "../dialogs/FreezeAccountDialog";
import UnFreezeAccountDialog from "../dialogs/UnFreezeAccountDialog";

export default function AccountSettings() {
    const sharedState = useSelector((state: RootState) => state.shared);

    const [isFetchingRequirement, setIsFetchingRequirement] = useState(true);
    const [accountDetail, setAccountDetail] = useState<AccountDetailDto | null>(
        null
    );
    const [originalAccountSettings, setOriginalAccountSettings] =
        useState<AccountSettingsDto | null>(null);
    const [accountSettings, setAccountSettings] =
        useState<SetAccountSettingInput | null>(null);
    const [countDiff, setCountDiff] = useState<number | null>(null);
    const [requestInProgress, setRequestInProgress] = useState(false);

    const { openDialog } = useContext(DialogContext);
    const { showSnackbar } = useContext(SnackbarContext);
    const user = useContext(UserContext);

    const isOwner = user.role === 1;

    useEffect(() => {
        fetchAppSettings();
    }, []);

    const fetchAppSettings = () => {
        Promise.all([
            AccountService.getAccountSettings("1"),
            AccountService.getAccountDetail("1"),
        ]).then((r) => {
            setAccountSettings(r[0]);
            setOriginalAccountSettings(r[0]);
            setAccountDetail(r[1]);
            setIsFetchingRequirement(false);
        });
    };

    useEffect(() => {
        setCountDiff(null);

        if (
            accountSettings === null ||
            accountSettings.visitedProjectExpirationInMonths === null
        ) {
            return;
        }

        ProjectService.getNewProjectDiffForVisitedProjectExpiration(
            accountSettings.visitedProjectExpirationInMonths,
            "1"
        ).then((r) => {
            setCountDiff(r === 0 ? null : r);
        });
    }, [accountSettings]);

    const handleVisitedProjectThresholdChange = (
        event: React.MouseEvent<HTMLElement>,
        threshold: AccountSettingsExpirationInMonths | null
    ) => {
        if (threshold === null) {
            return;
        }
        const updatedAccountSettings = {
            ...accountSettings,
            visitedProjectExpirationInMonths: threshold,
        };
        setAccountSettings(updatedAccountSettings);
    };

    const toggleVisitedProjectExpiration = () => {
        const updatedAccountSettings = {
            ...accountSettings,
            visitedProjectExpirationInMonths:
                !accountSettings.visitedProjectExpirationInMonths
                    ? AccountSettingsExpirationInMonths.MONTH3
                    : null,
        };
        setAccountSettings(updatedAccountSettings);
    };

    const applyChanges = () => {
        setRequestInProgress(true);
        AccountService.setAccountSettings("1", accountSettings)
            .then((r) => {
                showSnackbar("تنظیمات با موفقیت ذخیره شد", "success");
                fetchAppSettings();
            })
            .finally(() => {
                setRequestInProgress(false);
            });
    };

    const openResetAccountDialog = () => {
        openDialog(<AccountResetDialog />);
    };

    const openFreezeAccountDialog = () => {
        openDialog(<FreezeAccountDialog />);
    };

    const disableAccountFreezeDialog = () => {
        openDialog(<UnFreezeAccountDialog />);
    };

    const handleActivityNotificationChange = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setAccountSettings({
            ...accountSettings,
            enableActivityNotification: event.target.checked,
        });
    };

    if (isFetchingRequirement) {
        return <Spinner />;
    }

    return (
        <Container maxWidth="xs">
            <PageHeader title="تنظیمات" />

            {!isOwner && (
                <Alert severity="error">
                    تنها صاحب حساب کاربری به بخش تنظیمات پروژه ها دسترسی دارد
                </Alert>
            )}

            {isOwner && (
                <Stack spacing={2}>
                    <Paper sx={{ p: 2 }}>
                        <Stack spacing={2}>
                            {!accountDetail.isExpired ? (
                                <Alert severity="success">
                                    اشتراک شما فعال است و در تاریخ{" "}
                                    {toJalaliDate(accountDetail.endDate)} به
                                    پایان میرسد
                                </Alert>
                            ) : (
                                <Alert severity="error">
                                    اشتراک شما غیر فعال است و در تاریخ{" "}
                                    {toJalaliDate(accountDetail.endDate)} پایان
                                    یافته
                                </Alert>
                            )}

                            <Divider flexItem />

                            <Stack direction="row" alignItems="center">
                                <Checkbox
                                    checked={
                                        accountSettings.enableActivityNotification
                                    }
                                    onChange={handleActivityNotificationChange}
                                />

                                <Typography>
                                    فعال کردن ارسال پیامک برای فعالیت ها
                                </Typography>
                            </Stack>

                            {accountSettings.enableActivityNotification && (
                                <Alert severity="warning">
                                    ارسال پیامک برای فعالیت های فرایند فروش از
                                    شارژ پیامکی استفاده می کند لذا از داشتن شارژ
                                    کافی اطمینان حاصل نمایید.
                                </Alert>
                            )}

                            <Divider />
                        </Stack>

                        <LoadingButton
                            fullWidth
                            sx={{ mt: 2 }}
                            variant="contained"
                            onClick={applyChanges}
                            loading={requestInProgress}
                            color="primary"
                        >
                            ذخیره تغییرات
                        </LoadingButton>
                    </Paper>

                    {/* <Button
                        variant="outlined"
                        color="warning"
                        onClick={openResetAccountDialog}
                    >
                        حذف انتخابی اطلاعات حساب کاربری
                    </Button> */}

                    {sharedState.accountDetail.subscriptionDuration >= 12 &&
                        !accountDetail.isFrozen && (
                            <Button
                                variant="outlined"
                                color="warning"
                                onClick={openFreezeAccountDialog}
                            >
                                فریز کردن حساب کاربری
                            </Button>
                        )}

                    {accountDetail.isFrozen && (
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={disableAccountFreezeDialog}
                        >
                            غیر فعال کردن فریز
                        </Button>
                    )}
                </Stack>
            )}
        </Container>
    );
}
